/* eslint  no-undef: "off"*/
import {
  BatchSpanProcessor,
  SamplingDecision,
  WebTracerProvider
} from "@opentelemetry/sdk-trace-web";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";

export const collectorOptions = {
  url: `${import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT}/v1/traces`,
  headers: {
    mode: "cors",
    "Referrer-Policy": "origin"
  },
  concurrencyLimit: 10 // an optional limit on pending requests
};

export const getResource = () => {
  return resourceFromAttributes({
    [ATTR_SERVICE_NAME]: "opc-digital-signage",
    [ATTR_SERVICE_VERSION]: "1.0.0", //Update when/if we start a release process
    ddsource: "webjs",
    "deployment.environment": import.meta.env.VITE_OTEL_DEPLOYMENT_ENVIRONMENT ?? "local"
  });
};

export function initializeOpenTelemetry() {
  const exporter = new OTLPTraceExporter(collectorOptions);

  const provider = new WebTracerProvider({
    resource: getResource(),
    spanProcessors: [
      new BatchSpanProcessor(exporter, {
        // The maximum queue size. After the size is reached spans are dropped.
        maxQueueSize: 100,
        // The maximum batch size of every export. It must be smaller or equal to maxQueueSize.
        maxExportBatchSize: 10,
        // The interval between two consecutive exports
        scheduledDelayMillis: 500,
        // How long the export can run before it is cancelled
        exportTimeoutMillis: 30000
      })
    ],
    sampler: {
      //eslint-disable-next-line no-unused-vars
      shouldSample: (context, traceId, spanName, spanKind, attributes, links) => {
        //Note that the sampling and additional post processing doesn't occur during HMR for some reason.
        //If you fully refresh the page, you'll see these work as expected.
        let decision = SamplingDecision.RECORD_AND_SAMPLED;
        if (!includeSpan(spanName)) {
          decision = SamplingDecision.NOT_RECORD;
        }

        return { decision };
      }
    }
  });

  provider.register({
    contextManager: new ZoneContextManager()
  });

  // Registering instrumentations
  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations({
        "@opentelemetry/instrumentation-document-load": {
          applyCustomAttributesOnSpan: {
            documentLoad: (span) => {
              decorateSpan("DOCUMENT LOAD", span);
            },
            documentFetch: (span) => {
              decorateSpan("DOCUMENT FETCH", span);
            },
            resourceFetch: (span) => {
              decorateSpan("RESOURCE FETCH", span);
            }
          }
        }
      })
    ]
  });
}

const decorateSpan = (spanLabel, span) => {
  if (span.isRecording()) {
    const pathName = new URL(span.attributes["http.url"]).pathname;
    span.updateName(buildSpanNameFromPath(spanLabel, pathName));
  }
};

export const buildSpanNameFromPath = (prefix, url) => {
  return `${prefix} ${url}`;
};

//Certain auto-instrumentation spans are VERY noisy so we can configure removing
//them to take the load off the otel collector
const includeSpan = (spanName) => {
  const skipSpans = (import.meta.env.VITE_SKIP_SPAN_RECORDING ?? "").split(",");
  if (skipSpans.includes(spanName)) {
    return false;
  } else return true;
};
